import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@ibe/ui';
import { useState } from 'react';

import { useAppForm } from '../../../lib/forms/use-app-form.js';
import {
  PMS_CREDENTIAL_DEFAULTS,
  PmsCredentialFields,
  pmsCredentialFormInput,
  useCreatePmsCredential,
} from '../../pms-credentials/index.js';
import { type PmsCredentialFormInput } from '../../pms-credentials/index.js';

export interface CreatedPmsCredential {
  readonly id: string;
  readonly name: string;
}

export interface CreatePmsCredentialDialogProps {
  readonly children: React.ReactNode;
  readonly onCreated: (credential: CreatedPmsCredential) => void;
}

export function CreatePmsCredentialDialog({
  children,
  onCreated,
}: CreatePmsCredentialDialogProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const create = useCreatePmsCredential();

  const form = useAppForm<PmsCredentialFormInput>({
    schema: pmsCredentialFormInput,
    defaultValues: PMS_CREDENTIAL_DEFAULTS,
    successMessage: 'PMS credential created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      onCreated({ id: created.id, name: values.name });
    },
    onSuccess: () => {
      setOpen(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New PMS credential</DialogTitle>
          <DialogDescription>Add a PMS credential to use in this property setup.</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void form.submit();
          }}
          className="space-y-6"
        >
          <PmsCredentialFields
            register={form.register}
            errors={form.formState.errors}
            control={form.control}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={form.submitting}
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.submitting}>
              {form.submitting ? 'Creating…' : 'Create credential'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
